import { z } from 'zod';
import {
  ExtensionDataClient,
  getEcomRequiredApps,
} from '@wix/extensions-data-client';
import type { WidgetPlugin } from '@wix/extensions-data-client';
import { DevCenterClient } from '@wix/dev-center-client';
import { getSitePluginExtensionsDir } from '@wix/cli-astro-definitions';
import {
  folderExistsMessage,
  pathAvailableValidation,
} from '../../../dev-center/components/validations/common.js';
import { createCustomElementFolderValidation } from '../../../dev-center/components/validations/custom-element.js';
import type { ExtensionDef } from '../generate-schema/types.js';
import { defineField } from '../generate-schema/definitions.js';
import { enrichCustomElementWithDerivedFolder } from '../utils/derive-folder.js';
import { generateSitePlugin } from './generate.js';

function buildWidgetPluginSlots(plugins: WidgetPlugin[]) {
  const slotOptions = plugins.flatMap((wp) =>
    wp.component.data.widgetPlugin.placements.map((p) => ({
      value: `${wp.component.componentId}:${p.slotId}`,
      title: `${wp.component.name} — ${p.slotId}`,
    }))
  );

  return slotOptions.map((opt) =>
    z.literal(opt.value).describe(defineField({ title: opt.title }))
  ) as [
    z.ZodLiteral<string>,
    z.ZodLiteral<string>,
    ...Array<z.ZodLiteral<string>>,
  ];
}

function parseSitePluginSlotId(slotId: string): {
  widgetId: string;
  slotId: string;
} {
  const colonIndex = slotId.indexOf(':');
  return {
    widgetId: slotId.substring(0, colonIndex),
    slotId: slotId.substring(colonIndex + 1),
  };
}

export const sitePlugin: ExtensionDef = {
  title: 'Site Plugin',
  description: 'A plugin that extends a Wix app widget with a custom component',
  group: 'Site',
  documentation:
    'https://dev.wix.com/docs/wix-cli/guides/extensions/site-extensions/site-plugins/add-a-site-plugin-extension',
  zodSchema: async (httpClient) => {
    const client = new ExtensionDataClient(httpClient);
    const plugins = await client.getWidgetPlugins();

    const slotLiterals = buildWidgetPluginSlots(plugins);

    return z.object({
      extensionType: z.literal('SITE_PLUGIN'),
      slotId: z.union(slotLiterals).describe(
        defineField({
          title: 'Slot',
          description: 'The widget slot to extend. Format: componentId:slotId.',
        })
      ),
      name: z.string().describe(defineField({ title: 'Plugin name' })),
      folder: z
        .string()
        .regex(/^[a-z][a-z0-9]*(-[a-z0-9]+)+$/)
        .optional()
        .describe(
          defineField({
            title: 'Plugin folder',
            description:
              "Must be a valid custom element name: lowercase, starts with a letter, contains at least one hyphen. Derived from the plugin name when omitted (suffixed with '-plugin' when needed).",
          })
        ),
    });
  },
  enrich: (data) =>
    enrichCustomElementWithDerivedFolder(data, {
      sourceField: 'name',
      suffix: 'plugin',
    }),
  validate: async (projectFolder, data) => {
    const dir = getSitePluginExtensionsDir(projectFolder);
    if (!pathAvailableValidation(dir)(data.folder)) {
      return folderExistsMessage('plugin', data.folder, 'name');
    }
    const result = createCustomElementFolderValidation(dir)(data.folder);
    return result.valid ? null : result.reason;
  },
  generate: async ({ projectFolder, appId, data, httpClient }) => {
    const { widgetId, slotId } = parseSitePluginSlotId(data.slotId);

    const plugins = await new ExtensionDataClient(
      httpClient
    ).getWidgetPlugins();
    const widgetPlugin = plugins.find(
      (wp) => wp.component.componentId === widgetId
    );

    const ecomRequiredApps = await getEcomRequiredApps();
    const requireAppId =
      ecomRequiredApps.appId === widgetPlugin?.appId
        ? ecomRequiredApps.requiredApps[0].appId
        : widgetPlugin?.appId;

    const placement = plugins
      .find((wp) => wp.component.componentId === widgetId)
      ?.component.data.widgetPlugin.placements.find((p) => p.slotId === slotId);

    if (!placement) {
      throw new Error(`Unknown slot: ${data.slotId}`);
    }

    if (requireAppId) {
      await new DevCenterClient(httpClient).addRequiredApp({
        appId,
        appIdToRequire: requireAppId,
      });
    }

    return generateSitePlugin(projectFolder, data.folder, data.name, [
      placement,
    ]);
  },
};
